import { Component, OnInit, DoCheck, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ViewChild, ElementRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { VgApiService } from '@videogular/ngx-videogular/core';
import { AnalyticEvent } from 'app/lib/analytic-event';
import { LogService } from 'app/lib/log.service';
import { SeriesService } from 'app/lib/series.service';
import { voice } from 'app/models/course';
import { UtilityService } from 'app/lib/utility.service';
import { Location } from '@angular/common';
import { User } from 'app/models/user';
import { UserService } from 'app/lib/user.service';
import { ApiService } from 'app/lib/api.service';
@Component({
    selector: 'app-series-voices-player',
    templateUrl: './series-voices-player.component.html',
    styleUrls: ['./series-voices-player.component.scss'],
    standalone: false,
})
export class SeriesVoicesPlayerComponent implements OnInit, DoCheck {
    loaded: boolean = false;
    voiceList: any = [];
    @ViewChild('audioPlayer') audioPlayer: ElementRef;
    voiceId: any;

    playing = false;
    voiceVolume = 50;
    voiceType: string = 'none';
    timer: any;
    lang: string = 'en';
    currentVoice: any = {};
    isPlaying: boolean = false;
    selectedStarIndex: number = 0;
    rating: number = 0;
    currentTime: any = 0;
    api: VgApiService;
    transcriptExpanded: boolean = false;
    user: User;
    isAudioLoaded: boolean = false;
    showBreadcrumbs: boolean = true;
    organizationOwnContentTitle: string = '';
    isOrgContent: boolean = false;
    volumeTrackBackground = '';
    constructor(
        private translate: TranslateService,
        private log: LogService,
        private userService: UserService,
        private activatedRoute: ActivatedRoute,
        private seriesService: SeriesService,
        private utilityService: UtilityService,
        private location: Location,
        private router: Router,
        private apiService: ApiService,
        private cdr: ChangeDetectorRef
    ) {
        this.voiceType = this.activatedRoute.snapshot.paramMap.get('voiceType');
        this.voiceId = this.activatedRoute.snapshot.paramMap.get('voiceId');
    }

    ngOnInit() {
        this.resetScroll();
        this.lang = this.translate.currentLang;
        this.user = this.userService.getUser();
        this.showBreadcrumbs = this.user?.userType === 'user' ? true : false;
        this.organizationOwnContentTitle =
            this.user?.primaryOrganization?.settings[
                'organizationOwnContentTitle' +
                    this.lang.replace(/^\w/, c => c.toUpperCase())
            ] || '';
        if (location.pathname.includes('/org-series-contents')) {
            this.getOrgVoices();
            this.isOrgContent = true;
        } else {
            this.getStories();
            this.isOrgContent = false;
        }
        this.setRating();
        this.updateVolumeBackground(this.voiceVolume);
    }

    resetScroll() {
        // Immediately reset window scroll
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;

        // Try multiple times with increasing delays to ensure scroll resets
        [0, 50, 100, 150].forEach(delay => {
            setTimeout(() => {
                window.scrollTo(0, 0);
                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;

                const detailsSection = document.querySelector(
                    '.details-top-section'
                );
                if (detailsSection) {
                    detailsSection.scrollTop = 0;
                }

                const appPage = document.querySelector('.app-l-page');
                if (appPage) {
                    appPage.scrollTop = 0;
                }

                const main = document.querySelector('main');
                if (main) {
                    main.scrollTop = 0;
                }
            }, delay);
        });
    }
    ngDoCheck() {
        this.lang = this.translate.currentLang;
    }
    get displayText() {
        return this.transcriptExpanded
            ? this.currentVoice['transcript_' + this.lang]
            : this.utilityService.truncateTextToTwoLines(
                  this.currentVoice['transcript_' + this.lang]
              );
    }

    getStories() {
        if (this.voiceType === 'stories') {
            this.seriesService.voiceStorySeries.subscribe((data: any) => {
                if (!data || data.status == 'error') return;
                if (data.status == 'success') {
                    this.setVoiceSeries(data.seriesList);
                }
            });
            this.seriesService.getVoiceStorySeries();
        } else {
            this.seriesService.voiceBurningQuestionSeries.subscribe(
                (data: any) => {
                    if (!data || data.status == 'error') return;
                    if (data.status == 'success') {
                        this.setVoiceSeries(data.seriesList);
                    }
                }
            );
            this.seriesService.getBurningQuestionsSeries();
        }
    }
    setVoiceSeries(series: any) {
        let voices = [];
        if (!Object.keys(series).length) {
            return;
        } else {
            this.loaded = true;
            voices = voice.initializeArray(series, this.voiceType);
        }
        this.getSelectedVoice(voices);
    }
    getSelectedVoice(voices) {
        this.currentVoice = voices?.find(item => item.id == this.voiceId);
        this.currentVoice?.courseProgress &&
            (this.currentVoice.courseProgress['Status'] = 'inprogress');
        this.voiceList = voices?.filter(
            voice => voice.category === this.currentVoice.category
        );
    }
    voiceVolumeChange(value: any) {
        this.voiceVolume = value;
        let newVol = 0.0;
        newVol = value / 100;
        this.api.volume = newVol;
        this.updateVolumeBackground(value);
    }
    updateVolumeBackground(value: number): void {
        const percent = Number(value) || 0;
        this.volumeTrackBackground = `linear-gradient(to right, #7c5cbf 0%, #7c5cbf ${percent}%, #d9d9d9 ${percent}%, #d9d9d9 100%)`;
        this.cdr.detectChanges();
    }

    onPlayerReady(api: VgApiService) {
        this.api = api;
        this.api.currentTime = +this.currentTime / 1000;

        this.api.getDefaultMedia().subscriptions.play.subscribe(() => {
            this.log.debug('Playing started.');
            this.isPlaying = true;
            this.timer && clearInterval(this.timer);
            this.timer = setInterval(() => {
                this.updateCurrentTimeProgress();
            }, 8000);
        });
        this.api.getDefaultMedia().subscriptions.playing.subscribe(() => {
            this.currentVoice?.courseProgress &&
                (this.currentVoice.courseProgress['Status'] = 'inprogress');
            this.log.debug('Playing.');
        });
        this.api
            .getDefaultMedia()
            .subscriptions.loadedData.subscribe(loadedData => {
                this.isAudioLoaded = true;
            });
        this.api
            .getDefaultMedia()
            .subscriptions.timeUpdate.subscribe(eeee => {});

        this.api.getDefaultMedia().subscriptions.pause.subscribe(() => {
            this.log.debug('Playing pause.');
            this.playing = false;
            clearInterval(this.timer);
        });

        this.api.getDefaultMedia().subscriptions.ended.subscribe(() => {
            if (this.api.fsAPI.isFullscreen && this.api.fsAPI.isAvailable) {
                this.api.fsAPI.toggleFullscreen();
            }
            this.log.debug('Playing end.');
            this.log.event(AnalyticEvent.commonEvents.voiceEnded, '', {
                [this.voiceType]: this.currentVoice.title_en,
            });
            clearInterval(this.timer);
            this.isPlaying = false;
            this.voiceEnded();
        });
    }
    voiceEnded() {
        this.playing = false;
        this.api.currentTime = 0;
        this.currentVoice.courseProgress['Status'] = 'completed';
        this.seriesService.updateAudioProgress(
            { current: 0, left: 0 },
            this.currentVoice,
            this.voiceType
        );
    }
    onPlay() {
        if (this.playing) {
            this.api.pause();
            this.log.event(AnalyticEvent.commonEvents.voicePaused, '', {
                [this.voiceType]: this.currentVoice.title_en,
            });
        } else {
            this.api.play();
            this.log.event(AnalyticEvent.commonEvents.voicePlayed, '', {
                [this.voiceType]: this.currentVoice.title_en,
            });
        }
        this.playing = !this.playing;
    }

    goBack() {
        this.location.back();
    }

    setRating() {
        this.rating = this.currentVoice.rating;
        this.selectedStarIndex = Math.floor(this.rating);
    }
    rateVoice(index: number) {
        this.selectedStarIndex = index;
        this.rating = index;
        if (this.currentVoice.rating == this.rating) {
            return;
        }
        if (this.playing) {
            this.onPlay();
        }
        this.seriesService.rateAudioSeries(
            this.rating,
            this.currentVoice,
            this.voiceType
        );
    }
    changeVoiceSeries(voiceData: voice) {
        let goToUrl = this.isOrgContent
            ? '/app/series/org-series-contents/voices/'
            : '/app/series/voices/';
        if (voiceData.id != this.currentVoice.id) {
            this.updateCurrentTimeProgress();
            this.playing = false;
            this.isPlaying = false;
            this.isAudioLoaded = false;
            this.api.pause();
            this.timer && clearInterval(this.timer);
            this.api.currentTime = 0;

            this.router.navigate([
                goToUrl + this.voiceType + '/' + voiceData.id,
            ]);
            this.resetScroll();
            this.voiceId = voiceData.id;
            this.currentVoice = voiceData;
            this.currentVoice?.courseProgress &&
                (this.currentVoice.courseProgress['Status'] = 'inprogress');
            this.setRating();
            this.audioPlayer.nativeElement.load();
            this.log.event(
                AnalyticEvent.internalPagesEvents.voicePlayerPage,
                '',
                {
                    [this.voiceType]: this.currentVoice.title_en,
                }
            );
        }
    }
    updateCurrentTimeProgress() {
        this.playing &&
            this.seriesService.updateAudioProgress(
                this.api.time,
                this.currentVoice,
                this.voiceType
            );
    }

    isExpanded = false;
    toggleText() {
        this.isExpanded = !this.isExpanded;
    }
    getOrgVoices() {
        const payLoad = {
            CourseType: 'organization',
        };

        const isUser = this.user.userType !== 'superadmin';

        const typeMap: Record<string, { user: string; admin: string }> = {
            burningQuestions: {
                user: 'burningquestions',
                admin: 'burningquestion',
            },
            stories: {
                user: 'stories',
                admin: 'story',
            },
        };

        const voiceType = typeMap[this.voiceType];

        if (!voiceType) {
            return;
        }

        const type = isUser ? voiceType.user : voiceType.admin;
        const url = isUser
            ? `series/${type}`
            : `series/${type}/${this.voiceId}`;

        this.apiService.get(url, payLoad).subscribe(
            (response: any) => {
                this.loaded = true;
                this.setVoiceSeries(response || []);
            },
            () => {
                this.loaded = false;
            }
        );
    }
    ngOnDestroy() {
    this.timer && clearInterval(this.timer);
    }
}
